import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { formatINR } from '../../data/products';
import { 
  Users, 
  Search, 
  Crown, 
  Phone, 
  Mail, 
  MapPin, 
  ShoppingBag, 
  Calendar, 
  Download, 
  Plus, 
  MessageSquare, 
  Award, 
  ShieldCheck, 
  Eye, 
  X, 
  FileSpreadsheet,
  AlertTriangle,
  Gift
} from 'lucide-react';

export default function AdminCustomers() {
  const { orders, consultations, addToast } = useShop();

  // Initial Sample / Real customer base derived from orders & consultations
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Synthesize rich customer records
  const [customers, setCustomers] = useState([
    {
      id: 'CUST-1001',
      name: 'Rohan Sharma',
      phone: '+91 98290 12345',
      email: 'rohan.sharma@gmail.com',
      city: 'Jaipur, Rajasthan',
      tier: 'Solitaire Club',
      totalSpent: 485000,
      ordersCount: 5,
      loyaltyPoints: 4850,
      lastOrderDate: '2026-10-04',
      status: 'Active',
      notes: 'Prefers 22KT antique polki bridal necklaces. Anniversay on 18 Nov.',
      abandonedCart: null
    },
    {
      id: 'CUST-1002',
      name: 'Pooja Agarwal',
      phone: '+91 94140 76543',
      email: 'pooja.agarwal@outlook.com',
      city: 'Delhi NCR',
      tier: 'Platinum Elite',
      totalSpent: 265000,
      ordersCount: 3,
      loyaltyPoints: 2650,
      lastOrderDate: '2026-09-28',
      status: 'Active',
      notes: 'Interested in Solitaire Diamond rings & lightweight gold chains.',
      abandonedCart: { item: 'Royal Rajputana Bridal Choker', value: 185000 }
    },
    {
      id: 'CUST-1003',
      name: 'Vikram Singh Shekhawat',
      phone: '+91 98280 99887',
      email: 'vikram.shekhawat@heritage.in',
      city: 'Jodhpur, Rajasthan',
      tier: 'Solitaire Club',
      totalSpent: 890000,
      ordersCount: 7,
      loyaltyPoints: 8900,
      lastOrderDate: '2026-10-08',
      status: 'Active',
      notes: 'Royal family client. Always requests hallmark certificate with HUID.',
      abandonedCart: null
    },
    {
      id: 'CUST-1004',
      name: 'Sunita Jain',
      phone: '+91 93140 33445',
      email: 'sunita.jain@yahoo.co.in',
      city: 'Mumbai, Maharashtra',
      tier: 'Gold Member',
      totalSpent: 115000,
      ordersCount: 2,
      loyaltyPoints: 1150,
      lastOrderDate: '2026-08-15',
      status: 'Active',
      notes: 'Buys 999 pure silver utensils for Diwali and Dhanteras.',
      abandonedCart: null
    },
    {
      id: 'CUST-1005',
      name: 'Amitabh Verma',
      phone: '+91 97110 55667',
      email: 'amitabh.verma@techmail.com',
      city: 'Bengaluru, Karnataka',
      tier: 'Silver Club',
      totalSpent: 42000,
      ordersCount: 1,
      loyaltyPoints: 420,
      lastOrderDate: '2026-09-12',
      status: 'Active',
      notes: 'Online buyer. Purchased platinum everyday band.',
      abandonedCart: { item: '18KT Rose Gold Tennis Bracelet', value: 78000 }
    }
  ]);

  // New Customer Form State
  const [newCustomerForm, setNewCustomerForm] = useState({
    name: '',
    phone: '',
    email: '',
    city: 'Jaipur',
    tier: 'Gold Member',
    notes: ''
  });

  const filteredCustomers = customers.filter(c => {
    const matchesSearch = 
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone.includes(searchTerm) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.city.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = tierFilter === 'all' || c.tier === tierFilter;
    return matchesSearch && matchesTier;
  });

  const handleExportCSV = () => {
    const headers = ['Customer ID,Name,Phone,Email,City,VIP Tier,Total Spent (INR),Orders,Loyalty Points,Last Order Date,Notes'];
    const rows = filteredCustomers.map(c => 
      `"${c.id}","${c.name}","${c.phone}","${c.email}","${c.city}","${c.tier}","${c.totalSpent}","${c.ordersCount}","${c.loyaltyPoints}","${c.lastOrderDate}","${c.notes || ''}"`
    );
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers, ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `ML_Jewellers_CRM_Customers_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    addToast?.('Customer database exported to CSV successfully', 'success');
  };

  const handleWhatsApp = (customer) => {
    const text = encodeURIComponent(`Namaste ${customer.name} ji, Greetings from M.L. Jewellers & Kitchenware Jaipur! We have exclusive curated bridal collections and private bullion rates available for our ${customer.tier} members.`);
    window.open(`https://api.whatsapp.com/send?phone=${customer.phone.replace(/[^0-9]/g, '')}&text=${text}`, '_blank');
  };

  const handleCreateCustomer = (e) => {
    e.preventDefault();
    if (!newCustomerForm.name || !newCustomerForm.phone) {
      addToast?.('Please enter Customer Name and Phone Number', 'error');
      return;
    }
    const created = {
      id: `CUST-${1000 + customers.length + 1}`,
      name: newCustomerForm.name.trim(),
      phone: newCustomerForm.phone.trim(),
      email: newCustomerForm.email.trim() || 'walkin@mljewellers.com',
      city: newCustomerForm.city.trim() || 'Jaipur',
      tier: newCustomerForm.tier,
      totalSpent: 0,
      ordersCount: 0,
      loyaltyPoints: 100, // Welcome bonus
      lastOrderDate: new Date().toISOString().slice(0, 10),
      status: 'Active',
      notes: newCustomerForm.notes.trim(),
      abandonedCart: null
    };
    setCustomers([created, ...customers]);
    setIsAddModalOpen(false);
    setNewCustomerForm({ name: '', phone: '', email: '', city: 'Jaipur', tier: 'Gold Member', notes: '' });
    addToast?.(`Customer ${created.name} enrolled in CRM successfully`, 'success');
  };

  return (
    <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
      
      {/* Top Banner */}
      <div style={{
        background: '#ffffff',
        borderRadius: '8px',
        padding: '1.25rem 1.6rem',
        border: '1px solid #e0e0e0',
        marginBottom: '1.5rem',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#2874f0', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '0.2rem' }}>
            <Users size={14} />
            <span>VIP CLIENT RELATIONSHIP MANAGEMENT</span>
          </div>
          <h2 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#212121', margin: 0 }}>
            Customer Directory & Loyalty Hub
          </h2>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#616161' }}>
            Track client spending records, Solitaire VIP tiers, bullion purchase history, and abandoned vault carts.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setIsAddModalOpen(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.55rem 1rem',
              borderRadius: '4px',
              border: 'none',
              background: '#2874f0',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '0.82rem',
              cursor: 'pointer'
            }}
          >
            <Plus size={15} />
            <span>New Customer</span>
          </button>

          <button
            onClick={handleExportCSV}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.55rem 1rem',
              borderRadius: '4px',
              border: '1px solid #2e7d32',
              background: '#f0fdf4',
              color: '#15803d',
              fontWeight: 700,
              fontSize: '0.82rem',
              cursor: 'pointer'
            }}
          >
            <Download size={15} />
            <span>Export to Excel/CSV</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'TOTAL ACTIVE CLIENTS', val: customers.length, sub: 'All verified profiles', color: '#2874f0' },
          { label: 'SOLITAIRE CLUB (VIP)', val: customers.filter(c => c.tier === 'Solitaire Club').length, sub: '₹5L+ High Net Worth', color: '#8b5cf6' },
          { label: 'LIFETIME SPEND VALUE', val: formatINR(customers.reduce((acc, c) => acc + c.totalSpent, 0)), sub: 'Consolidated bullion revenue', color: '#15803d' },
          { label: 'ABANDONED VAULT CARTS', val: customers.filter(c => c.abandonedCart).length, sub: 'Ready for WhatsApp recovery', color: '#d97706' }
        ].map(k => (
          <div key={k.label} style={{ background: '#ffffff', padding: '1.1rem 1.3rem', borderRadius: '8px', border: '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#757575', letterSpacing: '0.5px' }}>{k.label}</div>
            <div style={{ fontSize: '1.45rem', fontWeight: 800, color: k.color, margin: '0.3rem 0' }}>{k.val}</div>
            <div style={{ fontSize: '0.72rem', color: '#9e9e9e' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Filters Bar */}
      <div style={{ background: '#ffffff', borderRadius: '8px', padding: '0.9rem 1.2rem', border: '1px solid #e0e0e0', marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.8rem', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flex: 1, minWidth: '240px' }}>
          <Search size={16} color="#757575" />
          <input
            type="text"
            placeholder="Search by client name, mobile (+91), email or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', border: 'none', outline: 'none', fontSize: '0.85rem' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.78rem', color: '#757575', fontWeight: 700 }}>Filter Tier:</span>
          {['all', 'Solitaire Club', 'Platinum Elite', 'Gold Member', 'Silver Club'].map(tier => (
            <button
              key={tier}
              onClick={() => setTierFilter(tier)}
              style={{
                padding: '0.3rem 0.65rem',
                borderRadius: '4px',
                border: 'none',
                background: tierFilter === tier ? '#2874f0' : '#f1f3f6',
                color: tierFilter === tier ? '#fff' : '#424242',
                fontWeight: 600,
                fontSize: '0.75rem',
                cursor: 'pointer'
              }}
            >
              {tier === 'all' ? 'All Tiers' : tier}
            </button>
          ))}
        </div>
      </div>

      {/* Customers Table */}
      <div style={{ background: '#ffffff', borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.84rem' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <th style={{ padding: '0.85rem 1rem' }}>Customer Profile</th>
              <th style={{ padding: '0.85rem 1rem' }}>VIP Tier</th>
              <th style={{ padding: '0.85rem 1rem' }}>Location</th>
              <th style={{ padding: '0.85rem 1rem' }}>Lifetime Spend</th>
              <th style={{ padding: '0.85rem 1rem' }}>Orders</th>
              <th style={{ padding: '0.85rem 1rem' }}>Loyalty Points</th>
              <th style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map((cust) => {
              const isSolitaire = cust.tier === 'Solitaire Club';
              const isPlatinum = cust.tier === 'Platinum Elite';
              return (
                <tr key={cust.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}>
                  <td style={{ padding: '0.85rem 1rem' }}>
                    <div style={{ fontWeight: 800, color: '#1e293b', fontSize: '0.9rem' }}>{cust.name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.15rem' }}>
                      <span>{cust.phone}</span>
                      <span>•</span>
                      <span>{cust.email}</span>
                    </div>
                    {cust.abandonedCart && (
                      <div style={{ marginTop: '0.35rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#fef3c7', color: '#b45309', padding: '0.15rem 0.5rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 700 }}>
                        <AlertTriangle size={11} /> Cart Pending: {cust.abandonedCart.item} ({formatINR(cust.abandonedCart.value)})
                      </div>
                    )}
                  </td>

                  <td style={{ padding: '0.85rem 1rem' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      padding: '0.2rem 0.6rem',
                      borderRadius: '4px',
                      fontSize: '0.74rem',
                      fontWeight: 800,
                      background: isSolitaire ? '#f5f3ff' : isPlatinum ? '#eff6ff' : '#f0fdf4',
                      color: isSolitaire ? '#7c3aed' : isPlatinum ? '#1d4ed8' : '#15803d',
                      border: isSolitaire ? '1px solid #ddd6fe' : isPlatinum ? '1px solid #bfdbfe' : '1px solid #bbf7d0'
                    }}>
                      <Crown size={12} />
                      {cust.tier}
                    </span>
                  </td>

                  <td style={{ padding: '0.85rem 1rem', color: '#475569' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <MapPin size={13} color="#94a3b8" />
                      <span>{cust.city}</span>
                    </div>
                  </td>

                  <td style={{ padding: '0.85rem 1rem', fontWeight: 800, color: '#0f172a' }}>
                    {formatINR(cust.totalSpent)}
                  </td>

                  <td style={{ padding: '0.85rem 1rem', color: '#334155', fontWeight: 600 }}>
                    {cust.ordersCount} orders
                  </td>

                  <td style={{ padding: '0.85rem 1rem' }}>
                    <span style={{ color: '#d97706', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Gift size={12} /> {cust.loyaltyPoints} Pts
                    </span>
                  </td>

                  <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleWhatsApp(cust)}
                        title="Send WhatsApp Concierge Message"
                        style={{
                          background: '#dcfce7',
                          border: '1px solid #86efac',
                          color: '#15803d',
                          padding: '0.35rem 0.55rem',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <MessageSquare size={14} />
                      </button>

                      <button
                        onClick={() => setSelectedCustomer(cust)}
                        title="View Customer 360 Profile"
                        style={{
                          background: '#f1f5f9',
                          border: '1px solid #cbd5e1',
                          color: '#334155',
                          padding: '0.35rem 0.55rem',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <Eye size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Customer 360 Modal */}
      {selectedCustomer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{ background: '#ffffff', borderRadius: '8px', maxWidth: '580px', width: '100%', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ background: '#1e3c72', color: '#fff', padding: '1.2rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontSize: '0.72rem', letterSpacing: '0.5px', opacity: 0.85, fontWeight: 700 }}>CUSTOMER 360° DOSSIER</span>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800 }}>{selectedCustomer.name}</h3>
              </div>
              <button onClick={() => setSelectedCustomer(null)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '1.4rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.2rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Contact Mobile</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>{selectedCustomer.phone}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Email Address</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>{selectedCustomer.email}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>VIP Club Tier</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#7c3aed' }}>{selectedCustomer.tier}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Lifetime Vault Purchases</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#15803d' }}>{formatINR(selectedCustomer.totalSpent)}</div>
                </div>
              </div>

              <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '1.2rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
                  Client Preferences & Special Dates
                </div>
                <p style={{ margin: 0, fontSize: '0.84rem', color: '#334155' }}>
                  {selectedCustomer.notes || 'No notes added yet.'}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <button
                  onClick={() => handleWhatsApp(selectedCustomer)}
                  style={{
                    flex: 1,
                    padding: '0.65rem',
                    borderRadius: '4px',
                    border: 'none',
                    background: '#25D366',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.84rem',
                    cursor: 'pointer'
                  }}
                >
                  Open WhatsApp Chat
                </button>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  style={{
                    padding: '0.65rem 1.2rem',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    color: '#334155',
                    fontWeight: 700,
                    fontSize: '0.84rem',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Customer Modal */}
      {isAddModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <form onSubmit={handleCreateCustomer} style={{ background: '#ffffff', borderRadius: '8px', maxWidth: '520px', width: '100%', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ background: '#2874f0', color: '#fff', padding: '1.1rem 1.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800 }}>Enrol New Client in Vault CRM</h3>
              <button type="button" onClick={() => setIsAddModalOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '1.3rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Customer Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Smt. Gayatri Devi"
                  value={newCustomerForm.name}
                  onChange={(e) => setNewCustomerForm({ ...newCustomerForm, name: e.target.value })}
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Mobile Number *</label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 98290 00000"
                    value={newCustomerForm.phone}
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, phone: e.target.value })}
                    style={{ width: '100%', padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>City / Region</label>
                  <input
                    type="text"
                    placeholder="Jaipur, Rajasthan"
                    value={newCustomerForm.city}
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, city: e.target.value })}
                    style={{ width: '100%', padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '0.8rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Email Address</label>
                  <input
                    type="email"
                    placeholder="client@gmail.com"
                    value={newCustomerForm.email}
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, email: e.target.value })}
                    style={{ width: '100%', padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>VIP Tier</label>
                  <select
                    value={newCustomerForm.tier}
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, tier: e.target.value })}
                    style={{ width: '100%', padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                  >
                    <option value="Solitaire Club">Solitaire Club (VIP)</option>
                    <option value="Platinum Elite">Platinum Elite</option>
                    <option value="Gold Member">Gold Member</option>
                    <option value="Silver Club">Silver Club</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Jewellery Preferences / Occasion Notes</label>
                <textarea
                  rows={2}
                  placeholder="e.g. Wedding shopping in Dec, prefers 18K rose gold diamond bangles"
                  value={newCustomerForm.notes}
                  onChange={(e) => setNewCustomerForm({ ...newCustomerForm, notes: e.target.value })}
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.6rem' }}>
                <button
                  type="submit"
                  style={{ flex: 1, padding: '0.65rem', borderRadius: '4px', border: 'none', background: '#2874f0', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  Save Customer Profile
                </button>
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  style={{ padding: '0.65rem 1rem', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

    </div>
  );
}
